export interface Definition {
  id: 'businessValue' | 'timeCriticality' | 'riskOpportunity' | 'jobSize' | 'confidence';
  title: string;
  shortHelp: string;
  body: string;
}

export const DEFINITIONS: readonly Definition[] = [
  {
    id: 'businessValue',
    title: 'Business Value',
    shortHelp: 'Direct revenue, cost reduction, or strategic value',
    body: `**Business Value** captures the size of the outcome if we deliver this. Think revenue gained, cost avoided, or strategic position improved — not effort and not how many people are asking for it.

Score on the Fibonacci scale relative to the other items in the portfolio:

- **1 — Trivial.** Nice to have. No measurable revenue, savings, or strategic effect.
- **3 — Small.** A modest improvement to a single team or workflow.
- **5 — Moderate.** Visible business impact: meaningful efficiency gain or a single new revenue lever.
- **8 — Large.** Material impact on a department or product line; tied to a stated business goal.
- **13 — Transformational.** Step-change in revenue, cost, or strategy; the kind of outcome a quarter is planned around.

If you find yourself reasoning about how hard it is to build, you're scoring Job Size, not Business Value.`,
  },
  {
    id: 'timeCriticality',
    title: 'Time Criticality',
    shortHelp: 'How fast does value decay if delayed?',
    body: `**Time Criticality** asks how much value evaporates the longer we wait. A high score means there's a window — a deadline, a market moment, a contract clock — and missing it costs us.

- **1 — No decay.** The opportunity is the same whether we ship next week or next quarter.
- **3 — Slow decay.** Some softening over a quarter or more; nothing dramatic.
- **5 — Moderate decay.** Noticeably worse outcome if we delay a few months.
- **8 — Sharp decay.** A specific window is closing within weeks — a contract, a season, a competitor move.
- **13 — Hard deadline.** A binding date drives the outcome; after it, the value drops to near zero.

Don't score "we want it soon" as time-critical. Time-critical means the value itself decays, not that the requestor is impatient.`,
  },
  {
    id: 'riskOpportunity',
    title: 'Risk / Opportunity',
    shortHelp: 'Risk reduction or opportunity enablement',
    body: `**Risk / Opportunity (RR / OE)** captures the secondary effects: risks this item reduces, or future opportunities it unlocks. It's the "even if the headline outcome were smaller, this is still worth doing because…" lever.

- **1 — None.** No notable risk reduction; no opportunity unlocked.
- **3 — Small.** Cleans up a minor risk or makes one related future thing slightly easier.
- **5 — Moderate.** Removes a meaningful risk or enables a clear future project.
- **8 — Significant.** Eliminates a known compliance, security, or operational risk; or unlocks a major capability.
- **13 — Critical.** Avoids an existential / high-severity risk, or unlocks a strategic capability the company couldn't otherwise pursue.

Use this for de-risking work, platform investments, and compliance items whose headline Business Value looks small but whose downside protection is large.`,
  },
  {
    id: 'jobSize',
    title: 'Job Size',
    shortHelp: 'Effort to deliver — bigger is more',
    body: `**Job Size** is your honest estimate of the effort to deliver. WSJF divides Cost of Delay by Job Size, so this is the denominator — a bigger number lowers the priority.

Calibrate against past work, not against other portfolio items:

- **1 — Tiny.** A day or two of one person's time. Almost no coordination.
- **3 — Small.** A week-ish for one person, or a couple of days of coordinated work.
- **5 — Medium.** A few weeks of focused work; some coordination across roles.
- **8 — Large.** Multi-week to multi-month effort; multiple specialists; integration work.
- **13 — Very large.** A quarter or more; coordinated across multiple teams; ambiguity remaining.

If everything is scoring 13, your portfolio isn't broken down enough — split the biggest items.`,
  },
  {
    id: 'confidence',
    title: 'Confidence',
    shortHelp: 'How sure are we about the scores?',
    body: `**Confidence** annotates the other four scores — it doesn't go into the WSJF math. It tells reviewers how much weight to put on the ranking.

- **High.** We have evidence for the scores: usage data, a clear sponsor, a known scope.
- **Medium.** We have a defensible hypothesis but limited evidence; scope is sketched, not detailed.
- **Low.** We're guessing — directionally useful but the scores could move a lot once we learn more.

Low-confidence items aren't bad — but they're flagged so the Leader can weigh them differently when setting Decision.`,
  },
];

export interface ScoringScaleContent {
  title: string;
  body: string;
}

export const SCORING_SCALE: ScoringScaleContent = {
  title: 'Scoring scale',
  body: `Flow uses a **modified Fibonacci scale** for the four scoring dimensions: **1, 3, 5, 8, 13**. The gap between numbers grows on purpose — it forces you to choose between "small" and "medium" instead of splitting hairs between 4 and 5.

**WSJF** ranks items by Cost of Delay divided by Job Size:

\`\`\`
WSJF = (Business Value + Time Criticality + Risk/Opportunity) / Job Size
\`\`\`

Higher WSJF means schedule this sooner. The math rewards items that are small *and* high-value; it penalizes large items even when they're important.

**Priority Band** is derived from WSJF: items in the top tier of the portfolio are *High*, the middle is *Medium*, the bottom is *Low*. A Leader can override the band on a specific item when the math misses something important (e.g., a hard compliance deadline).`,
};

export function getDefinition(id: Definition['id']): Definition | undefined {
  return DEFINITIONS.find((d) => d.id === id);
}
